import { Duration } from 'luxon';
import i18next from 'i18next';
import { isEmpty, partition, uniqueId } from 'lodash/fp';
import { ErrorLevel, ERROR_CATEGORY } from 'app/lib/constants';
import { createError, translateError } from 'app/lib/error';
import { JobRunStatus } from 'app/lib/types/server';

export const getTagsBySpaceUid = (tags) =>
    tags.reduce((acc, tag) => {
        const { projectId, projectName, modelId, modelName } = tag;
        if (!acc[projectId]) {
            acc[projectId] = {
                name: projectName,
                projectId,
                nodes: {}
            };
        }
        if (!acc[projectId].nodes[modelId]) {
            acc[projectId].nodes[modelId] = {
                nodeName: modelName,
                nodeId: modelId,
                tags: []
            };
        }
        acc[projectId].nodes[modelId].tags.push(tag);

        return acc;
    }, {});

export const parseCheckResults = (result, level) => {
    const key = level === ErrorLevel.ERROR ? 'qualityCheckErrors' : 'qualityCheckInfos';

    return result.map(({ checkName, params }) => {
        const result = {
            id: uniqueId('check-result-'),
            type: `${key}.${checkName}`,
            category: ERROR_CATEGORY.dataQuality,
            level,
            values: {
                ...params,
                target: isEmpty(params?.target) ? 'Model' : params.target.table
            }
        };

        if (!isEmpty(params?.duration)) {
            const duration = params.duration;
            const durationString = duration ? Duration.fromISO(duration)?.toHuman() : '';
            result.values.duration = durationString;
        }
        if (checkName === 'ExpectValuesMatchSet') {
            const method = params.method;
            const values = params.values;
            result.type = `${result.type}_${method.replace(' ', '')}`;

            if (values.length > 3) {
                result.type = `${result.type}_other`;
                result.values.values = values.slice(0, 3).join(', ');
                result.values.count = values.length - 3;
            } else {
                result.values.values = values.slice(0, 3).join(', ');
            }
        }

        return result;
    });
};

export const getRunChecksResults = (jobRun) => {
    const checkResults = jobRun?.executionMetadata?.POST_CHECK_RESULTS ?? [];
    if (isEmpty(checkResults)) {
        return [];
    }
    const [passedChecks, failedChecks] = partition({ passed: true }, checkResults);
    const succeededStatuses = parseCheckResults(passedChecks, ErrorLevel.OK);
    const failedStatuses = parseCheckResults(failedChecks, ErrorLevel.ERROR);

    return [...failedStatuses, ...succeededStatuses];
};

export const getJobRunStatuses = (jobRun) => {
    if (isEmpty(jobRun)) {
        return [];
    }
    const statuses = [];
    const { executionMetadata, internalError, jobRunStatus, warnings } = jobRun;

    for (const warning of warnings ?? []) {
        statuses.push({
            type: 'GENERIC_WARNING',
            id: uniqueId('s'),
            level: ErrorLevel.WARNING,
            values: {
                message: translateError(i18next.t, warning)
            }
        });
    }

    if (!isEmpty(internalError)) {
        const error = createError(internalError.errorType, internalError);
        statuses.push({
            type: 'INTERNAL_ERROR',
            id: uniqueId('s'),
            level: ErrorLevel.ERROR,
            values: {
                message: translateError(i18next.t, error)
            }
        });
    }

    if (jobRunStatus === JobRunStatus.FAILED && !isEmpty(executionMetadata)) {
        statuses.push({
            type: 'SNOWFLAKE_ERROR',
            id: uniqueId('s'),
            level: ErrorLevel.ERROR,
            values: {
                code: executionMetadata.ERROR_CODE,
                message: executionMetadata.ERROR_MESSAGE
            }
        });
    }
    return statuses;
};
